import { Box, Link, Stack, styled, Typography } from '@mui/joy';

import { NeutralChip, TextEllipsis, TextLine } from 'core/elements';
import { parseString } from 'core/utils';
import {
  ExternalHazardousIcon,
  ExternalMarketingMemo,
  ExternalPartStatus,
  PartImage,
} from 'parts/elements';
import { PartNumberHelper } from 'parts/helpers';
import { IPart } from 'parts/types';
import { Color, Direction, Level } from 'theme/enums';

const PartWrapper = styled(Stack)(({ theme }) => ({
  alignItems: 'flex-start',
  gap: '10px',
  padding: '12px 16px',
  backgroundColor: theme.palette.neutral[50],
  border: `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: '6px',
  width: '100%',
}));

interface IProps {
  part: IPart;
  onClick: (part: IPart) => void;
  getPartCardButtons?: (part: IPart) => JSX.Element[];
  isAlternateVersionEnabled?: boolean;
}

export const PartCard = ({
  part,
  onClick,
  getPartCardButtons,
  isAlternateVersionEnabled = false,
}: IProps) => {
  return (
    <PartWrapper direction={Direction.ROW} key={`${part.id}-${part.manufacturerId}`}>
      {part.defaultImage && <PartImage url={part.defaultImage.url} alt={part.defaultImage.alt} />}
      <Stack flex={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isAlternateVersionEnabled && <ExternalHazardousIcon isHazardous={part.hazardous} />}
          <Link
            onClick={() => onClick(part)}
            level={Level.TITLE_MD}
            color={Color.PRIMARY}
            sx={{ textWrap: 'wrap' }}
          >
            {PartNumberHelper.toLabel(part.number)}
          </Link>
          {part.vendorNumber && (
            <NeutralChip>
              <Typography level={Level.TITLE_SM}>{part.vendorNumber}</Typography>
            </NeutralChip>
          )}
        </Box>
        {isAlternateVersionEnabled && (
          <>
            <ExternalPartStatus value={part.externalPartStatus} displayWarning />
            <ExternalMarketingMemo marketingMemo={part.marketingMemo} />
          </>
        )}
        <TextEllipsis
          level={Level.TITLE_SM}
          content={parseString(part.descriptionLong || part.description) as string}
          maxRows={3}
        />
        <TextLine
          color={Color.NEUTRAL}
          level={Level.TITLE_SM}
          text={part.manufacturerName}
          isSubText
          upperCase
        />
        <Stack direction={Direction.ROW} justifyContent="flex-end">
          {getPartCardButtons?.(part)}
        </Stack>
      </Stack>
    </PartWrapper>
  );
};
